// src/features/imports/selectors/imports.sort.ts

import type { ImportsFeatureState, ImportBucket } from "../model/imports.state.types";
import type { DriverImportId, VehicleImportId } from "../model/imports.state.types";

const BUCKET_ORDER: Record<ImportBucket, number> = {
  new: 0,
  existing: 1,
  ignored: 2,
};

function cmpString(a: string, b: string) {
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}

export function sortDriverImportIds(state: ImportsFeatureState, ids: DriverImportId[]): DriverImportId[] {
  const { readModel, view } = state;
  const { direction, key } = view.driverSort;
  const dir = direction === "asc" ? 1 : -1;

  return [...ids].sort((aId, bId) => {
    const a = readModel.driverItemsById[aId];
    const b = readModel.driverItemsById[bId];
    if (!a || !b) return 0;

    // 1) bucket order ALWAYS first: new -> existing -> ignored
    const bucketCmp = BUCKET_ORDER[a.bucket] - BUCKET_ORDER[b.bucket];
    if (bucketCmp !== 0) return bucketCmp;

    // 2) then the chosen key (usually fullName)
    if (key === "daysRead") {
      const numCmp = (a.daysRead - b.daysRead) * dir;
      if (numCmp !== 0) return numCmp;
      // tie-breaker
      return dir * cmpString(a.fullName, b.fullName);
    }

    if (key === "cardNumber") {
      const c = dir * cmpString(a.cardNumber, b.cardNumber);
      if (c !== 0) return c;
      return dir * cmpString(a.fullName, b.fullName);
    }

    // key === "fullName" | "bucket"
    // (bucket already applied first)
    return dir * cmpString(a.fullName, b.fullName);
  });
}

export function sortVehicleImportIds(state: ImportsFeatureState, ids: VehicleImportId[]): VehicleImportId[] {
  const { readModel, view } = state;
  const { direction, key } = view.vehicleSort;
  const dir = direction === "asc" ? 1 : -1;

  return [...ids].sort((aId, bId) => {
    const a = readModel.vehicleItemsById[aId];
    const b = readModel.vehicleItemsById[bId];
    if (!a || !b) return 0;

    // 1) bucket order ALWAYS first: new -> existing -> ignored
    const bucketCmp = BUCKET_ORDER[a.bucket] - BUCKET_ORDER[b.bucket];
    if (bucketCmp !== 0) return bucketCmp;

    // 2) then chosen key (usually registrationNumber)
    if (key === "daysRead") {
      const numCmp = (a.daysRead - b.daysRead) * dir;
      if (numCmp !== 0) return numCmp;
      return dir * cmpString(a.registrationNumber, b.registrationNumber);
    }

    // key === "registrationNumber" | "bucket"
    return dir * cmpString(a.registrationNumber, b.registrationNumber);
  });
}
